import type { SwSignal } from './swSignal';

export interface RunAfterSignal {
  /** Asks for one run at the next emit of the signal; asking again while it waits changes nothing. */
  ask(): void;
  /** Drops the wait: what was asked for is no longer wanted (the user or the project it was asked for is gone). */
  cancel(): void;
  isWaiting(): boolean;
}

/**
 * Something that goes on only once a fact happens (the config is applied, the
 * permissions are stored): however many ask before, it runs once, at the next
 * emit of the signal. The wait is cleared before the run, so a run that fails
 * (the signal logs it) is not retried: it asks again when it wants to.
 */
export function createRunAfterSignal(signal: SwSignal, run: () => void): RunAfterSignal {
  let stopWaiting: (() => void) | undefined;

  const cancel = (): void => {
    stopWaiting?.();
    stopWaiting = undefined;
  };

  return {
    ask: () => {
      stopWaiting ??= signal.subscribe(() => {
        cancel();
        run();
      });
    },
    cancel,
    isWaiting: () => stopWaiting !== undefined,
  };
}
